/**
 * Completion handshake for tools that write.
 *
 * Lifted from `GoogleChromeLabs/webmcp-tools/demos/react-flightsearch/src/webmcp.ts:3-32`,
 * with its UI half at `FlightSearch.tsx:30-37`.
 *
 * WHY THIS EXISTS. A write tool that resolves as soon as its service call
 * returns reports success while the page still shows the old rows. The agent
 * then re-reads, sees stale state, and either repeats the write or reports a
 * failure that did not happen. The tool therefore dispatches an event carrying
 * a `requestId` and resolves only when the view fires
 * `tool-completion-${requestId}` after it has committed and re-rendered.
 *
 * Two deliberate departures from the reference:
 *
 * 1. It resolves a `Settled` value instead of rejecting on timeout. Core's
 *    convention is that refusals are VALUES and exceptions are for bugs
 *    (see `contract.ts`), and a UI that did not confirm in time is an expected
 *    outcome, not a fault.
 * 2. The listener is removed on every path, including the success path before
 *    `resolve`, so a second dispatch with a recycled id cannot resolve a
 *    settled promise.
 *
 * The caller decides what a timeout means. For a write that already landed
 * server-side, `confirmed: false` means "the write happened, the view did not
 * confirm within the window" — which is a receipt caveat, not a failed write.
 */

export interface Settled {
  /** True when the view acknowledged the change before the deadline. */
  readonly confirmed: boolean;
  /** Correlation id, echoed so a receipt can cite the exact exchange. */
  readonly requestId: string;
  /** Milliseconds waited. Useful when tuning the deadline against a slow view. */
  readonly waitedMs: number;
}

/** Chosen to match the reference. A view that needs longer is the bug. */
export const DEFAULT_HANDSHAKE_TIMEOUT_MS = 5000;

function newRequestId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return Math.random().toString(36).slice(2, 15);
}

/**
 * Dispatches `eventName` with a `requestId` and waits for the view to fire
 * `tool-completion-${requestId}`.
 *
 * The view half is one line in the component that owns the data:
 *
 * ```ts
 * window.dispatchEvent(new CustomEvent(`tool-completion-${requestId}`));
 * ```
 *
 * fired AFTER the state the next read will hit has been updated — for Core
 * that means after the TanStack Query invalidation has resolved, not merely
 * after it was requested.
 */
export function dispatchAndWait(
  eventName: string,
  detail: Readonly<Record<string, unknown>> = {},
  timeoutMs: number = DEFAULT_HANDSHAKE_TIMEOUT_MS,
): Promise<Settled> {
  return new Promise<Settled>((resolve) => {
    const requestId = newRequestId();
    const completionEventName = `tool-completion-${requestId}`;
    const startedAt = Date.now();

    let done = false;
    const settle = (confirmed: boolean) => {
      if (done) return;
      done = true;
      window.clearTimeout(timeoutId);
      window.removeEventListener(completionEventName, handleCompletion);
      resolve({ confirmed, requestId, waitedMs: Date.now() - startedAt });
    };

    const handleCompletion = () => settle(true);
    const timeoutId = window.setTimeout(() => settle(false), timeoutMs);

    window.addEventListener(completionEventName, handleCompletion);
    window.dispatchEvent(new CustomEvent(eventName, { detail: { ...detail, requestId } }));
  });
}
